import { Table, CombinedTable, Reservation } from "@/types/table";
import { parseTimeToMinutes, doTimesOverlap } from "@/lib/timeBookingUtils";

// Re-export TablePosition and TableShape from the hook for backward compatibility
export type { TablePosition, TableShape } from "@/hooks/useFloorPlanPositions";
import type { TablePosition } from "@/hooks/useFloorPlanPositions";
import { reservationOverlapsSlot } from "@/lib/serviceOrder";

export type TableStatus = 'free' | 'reserved' | 'arrived' | 'late' | 'no-show' | 'cancelled' | 'inactive';

export interface LinkedTableInfo {
  isCombined: boolean;
  combinedTableName?: string;
  linkedTableNames: string[];
  reservation?: Reservation;
}

export interface TimeConflictOptions {
  bookingMode: 'time';
  reservationDurationMinutes: number;
  slotEnd?: string;
}

// Check if a reservation overlaps with a time slot (for TIME mode)
const doesReservationOverlapSlot = (
  reservationTime: string,
  slotStart: string,
  slotEnd: string,
  reservationDuration: number
): boolean => {
  // Identique à l'historique pour un créneau ordinaire ; gère les créneaux de nuit.
  return reservationOverlapsSlot(reservationTime.substring(0, 5), reservationDuration, slotStart, slotEnd);
};

// Get linked table info for a specific table
export const getLinkedTableInfo = (
  tableId: string,
  reservations: Reservation[],
  selectedTimeSlot: string | null,
  combinedTables: CombinedTable[],
  tables: Table[],
  conflictOptions?: TimeConflictOptions
): LinkedTableInfo => {
  if (!selectedTimeSlot) {
    return { isCombined: false, linkedTableNames: [] };
  }

  // Find a reservation on a combined table that includes this table
  for (const combinedTable of combinedTables) {
    if (!combinedTable.isActive) continue;
    
    const tableIds = combinedTable.tableIds || [];
    if (!tableIds.includes(tableId)) continue;
    
    // Check if there's a reservation for this combined table at this time slot
    const reservation = reservations.find(r => {
      const matchesTable = r.combinedTableId === combinedTable.id;
      
      let matchesTime: boolean;
      if (conflictOptions?.bookingMode === 'time' && conflictOptions.slotEnd) {
        // In TIME mode, check for overlap using per-reservation duration
        const resDuration = r.durationMinutes ?? conflictOptions.reservationDurationMinutes;
        matchesTime = doesReservationOverlapSlot(
          r.startTime,
          selectedTimeSlot,
          conflictOptions.slotEnd,
          resDuration
        );
      } else {
        // SLOT mode: exact match
        matchesTime = r.startTime === selectedTimeSlot || r.startTime.substring(0, 5) === selectedTimeSlot;
      }
      
      return matchesTable && matchesTime && r.status !== 'annulé';
    });
    
    if (reservation) {
      // Get names of other tables in this combination
      const linkedTableNames = tableIds
        .filter(id => id !== tableId)
        .map(id => {
          const table = tables.find(t => t.id === id);
          return table?.name || '';
        })
        .filter(name => name !== '');
      
      return {
        isCombined: true,
        combinedTableName: combinedTable.name,
        linkedTableNames,
        reservation,
      };
    }
  }
  
  return { isCombined: false, linkedTableNames: [] };
};

export const getTableStatus = (
  tableId: string,
  type: 'single' | 'combined',
  reservations: Reservation[],
  selectedTimeSlot: string | null,
  table: Table | CombinedTable | undefined,
  currentTime: Date,
  combinedTables?: CombinedTable[],
  conflictOptions?: TimeConflictOptions
): { status: TableStatus; reservation?: Reservation } => {
  if (!table) return { status: 'inactive' };
  if (!table.isActive) return { status: 'inactive' };
  if (!selectedTimeSlot) return { status: 'free' };
  
  // Helper to check if reservation matches time
  const matchesTimeSlot = (r: Reservation): boolean => {
    if (conflictOptions?.bookingMode === 'time' && conflictOptions.slotEnd) {
      const resDuration = r.durationMinutes ?? conflictOptions.reservationDurationMinutes;
      return doesReservationOverlapSlot(
        r.startTime,
        selectedTimeSlot,
        conflictOptions.slotEnd,
        resDuration
      );
    }
    return r.startTime === selectedTimeSlot || r.startTime.substring(0, 5) === selectedTimeSlot;
  };
  
  // Find direct reservation for this table at this time slot
  let reservation = reservations.find(r => {
    const matchesTable = type === 'single' 
      ? r.tableId === tableId 
      : r.combinedTableId === tableId;
    return matchesTable && matchesTimeSlot(r) && r.status !== 'annulé';
  });
  
  // For single tables, also check combined table reservations
  if (!reservation && type === 'single' && combinedTables) {
    for (const combinedTable of combinedTables) {
      if (!combinedTable.isActive) continue;
      const tableIds = combinedTable.tableIds || [];
      if (!tableIds.includes(tableId)) continue;
      
      reservation = reservations.find(r => {
        const matchesTable = r.combinedTableId === combinedTable.id;
        return matchesTable && matchesTimeSlot(r) && r.status !== 'annulé';
      });
      
      if (reservation) break;
    }
  }
  
  if (!reservation) return { status: 'free' };
  
  if (reservation.status === 'arrivé') {
    return { status: 'arrived', reservation };
  }
  
  if (reservation.status === 'annulé') {
    return { status: 'cancelled', reservation };
  }
  
  // Check if late (more than 15 minutes after start time)
  // In TIME mode, use the reservation's actual start time; in SLOT mode, use the slot start
  const timeToCheck = conflictOptions?.bookingMode === 'time' 
    ? reservation.startTime.substring(0, 5) 
    : selectedTimeSlot;
  const [hours, minutes] = timeToCheck.split(':').map(Number);
  const slotTime = new Date();
  slotTime.setHours(hours, minutes, 0, 0);
  
  const minutesLate = Math.floor((currentTime.getTime() - slotTime.getTime()) / 60000);

  if (minutesLate > 25) {
    return { status: 'no-show', reservation };
  }
  if (minutesLate > 15) {
    return { status: 'late', reservation };
  }

  return { status: 'reserved', reservation };
};

/**
 * Service-mode meta derived from the day's reservations on a table.
 * Used to show "libre HH:MM" on occupied tables, the next upcoming reservation,
 * and to flag double-service (two reservations close together).
 */
export interface TableServiceMeta {
  /** Next future reservation on this table (today, after now). */
  nextReservation?: Reservation;
  /** When the currently occupying reservation is expected to free up (HH:MM). */
  freeAt?: string;
  /** Minutes until the table frees up (positive number, only when occupied). */
  freeInMinutes?: number;
  /** A second reservation starts within 90 min of expected release. */
  hasDoubleService: boolean;
}

const HHMM = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

export const getTableServiceMeta = (
  current: Reservation | undefined,
  dayReservations: Reservation[] | undefined,
  currentTime: Date,
  defaultDurationMinutes: number = 120
): TableServiceMeta => {
  const list = (dayReservations ?? []).filter(r => r.status !== 'annulé');

  // Compute free-at if currently arrived
  let freeAt: string | undefined;
  let freeInMinutes: number | undefined;
  if (current && current.status === 'arrivé') {
    const [h, m] = current.startTime.substring(0, 5).split(':').map(Number);
    const start = new Date(currentTime);
    start.setHours(h, m, 0, 0);
    const dur = current.durationMinutes ?? defaultDurationMinutes;
    const end = new Date(start.getTime() + dur * 60000);
    freeAt = HHMM(end);
    freeInMinutes = Math.max(0, Math.round((end.getTime() - currentTime.getTime()) / 60000));
  }

  // Next future reservation strictly after now and not the current one
  const nowMin = currentTime.getHours() * 60 + currentTime.getMinutes();
  const sorted = [...list].sort((a, b) => a.startTime.localeCompare(b.startTime));
  const next = sorted.find(r => {
    if (current && r.id === current.id) return false;
    const [h, m] = r.startTime.substring(0, 5).split(':').map(Number);
    return h * 60 + m >= nowMin;
  });

  // Double service: another reservation starts within 90 min of expected release
  let hasDoubleService = false;
  if (current && freeAt && next) {
    const [fh, fm] = freeAt.split(':').map(Number);
    const [nh, nm] = next.startTime.substring(0, 5).split(':').map(Number);
    const diff = (nh * 60 + nm) - (fh * 60 + fm);
    if (diff <= 90) hasDoubleService = true;
  } else if (!current && list.length >= 2) {
    hasDoubleService = true;
  }

  return { nextReservation: next, freeAt, freeInMinutes, hasDoubleService };
};

export const getStatusColor = (status: TableStatus): string => {
  switch (status) {
    case 'free':
      return 'bg-green-50 border-green-300 text-green-700 dark:bg-green-950/30 dark:border-green-800 dark:text-green-300';
    case 'reserved':
      return 'bg-blue-500/20 border-blue-500 text-blue-700 dark:text-blue-400';
    case 'arrived':
      return 'bg-emerald-600 border-emerald-700 text-white dark:bg-emerald-700 dark:border-emerald-500 dark:text-white';
    case 'late':
      return 'bg-orange-500/20 border-orange-500 text-orange-700 dark:text-orange-400';
    case 'no-show':
      return 'bg-red-500/20 border-red-600 text-red-700 dark:text-red-400';
    case 'cancelled':
      return 'bg-muted/50 border-muted-foreground/30 text-muted-foreground';
    case 'inactive':
      return 'bg-muted/30 border-muted-foreground/20 text-muted-foreground/50';
    default:
      return 'bg-muted border-muted-foreground text-foreground';
  }
};

export const getStatusLabel = (status: TableStatus): string => {
  switch (status) {
    case 'free': return 'Libre';
    case 'reserved': return 'Réservé';
    case 'arrived': return 'Arrivé';
    case 'late': return 'En retard';
    case 'no-show': return 'No-show';
    case 'cancelled': return 'Annulé';
    case 'inactive': return 'Inactive';
    default: return '';
  }
};
